/**
 * Infinite-EXTEND-carrier constructors — split out of `trimExtendIntersection.ts`
 * (that file crossed the project's 2000-line hard ceiling, CLAUDE.md file-size
 * discipline). Pure structural extraction, no behaviour change — every function
 * here keeps its exact original body (including all mutation-testing
 * equivalent-mutant / best-effort-exhausted documentation), just relocated.
 *
 * Covers the per-entity-type "what does this target's carrier look like when
 * stretched to infinity for EXTEND" constructors: `fullCircleTessellation`
 * (bulge-terminal full-circle carrier), `stretchedEndPoint`/`extentMargin`
 * (generic straight-terminal tangent stretch + margin sizing shared by
 * several of the below), `polylineInfiniteCarrier`/`polylineInfiniteCarrierParts`
 * (LWPOLYLINE per-terminal dispatch), `stretchPolylineEndsToInfinite`
 * (generic dense-polyline both-ends stretch), `splineEndStretch` (NURBS
 * end-tangent stretch), and `infiniteLineSegment` (Line target's
 * stretched-both-ways carrier).
 *
 * Consumed by `trimExtendIntersection.ts`'s `entityIntersectionPoints` (via
 * `polylineInfiniteCarrierParts`) and `infiniteTargetPolyline` (via
 * `polylineInfiniteCarrier`, `stretchPolylineEndsToInfinite`,
 * `splineEndStretch`, `infiniteLineSegment`).
 */

import { bulgeArc, evaluateNurbs } from "@dxf-ai-editor/drawing-model";

import {
  CURVE_TESSELLATION_SAMPLES,
  GEO_EPS,
  rescaleKnotDomain,
  splineEndTangentStep,
  type IntPoint,
} from "./trimExtendGeometryCore";

/**
 * Dense sample of a FULL circle, walked forward (increasing angle) starting
 * at `startAngleRad` and ending back at the SAME physical point one full lap
 * later — used as a bulge-terminal LWPOLYLINE segment's infinite carrier.
 *
 * The start angle MATTERS beyond cosmetics: `polylineInfiniteCarrier`
 * concatenates this tessellation directly adjacent to the segment's own
 * `real` tessellated points (`[...prefix, ...real, ...suffix]`), and
 * `polyPolyIntersection` treats EVERY consecutive pair in that combined array
 * as a genuine line segment to test for crossings. A circle tessellation that
 * starts at a fixed angle (e.g. 0°) unrelated to where `real` actually begins
 * or ends creates a SPURIOUS "closing chord" cutting straight across the
 * circle's interior between the two disjoint samples — `polyPolyIntersection`
 * would report a false-positive "boundary crossing" for any EXTEND edge that
 * happens to cross that phantom chord (a point nowhere near the true circle).
 * Starting exactly at the real segment's own endpoint angle makes the join a
 * zero-length (or true-arc) connection instead, eliminating the phantom edge.
 */
function fullCircleTessellation(
  cx: number,
  cy: number,
  radius: number,
  startAngleRad: number,
): IntPoint[] {
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the `pts: IntPoint[] = []` ArrayDeclaration mutant: this function's
  // ONLY consumers (`polylineInfiniteCarrierParts`'s `prefix`/`suffix`) place
  // the ENTIRE returned array immediately adjacent to a genuine "zero-length
  // join" point (`real[0]`/`real[last]`, which EXACTLY equals this array's
  // own first/last sample by construction — see this function's own doc
  // comment) that itself already contributes NO crossing regardless (a
  // zero-length segment has no direction for `segSegIntersection` to test).
  // A single poisoned LEADING element only ever displaces THAT already-inert
  // join chord into two NaN-based (still inert) ones — it can never corrupt
  // any of the array's own N genuine 1-step arc chords. Verified directly:
  // disabling this guard (poisoning `pts`) leaves BOTH a dedicated
  // near-the-join probe AND this file's own broad exact-count probe (see
  // `trimExtendIntersection.mutation3.test.ts`) unchanged, and leaves every
  // OTHER test in this module's mutation suite passing unchanged too.
  // Stryker disable next-line ArrayDeclaration: a single poisoned leading element only ever displaces the already-inert zero-length join chord (see equivalent-mutant note above)
  const pts: IntPoint[] = [];
  for (let i = 0; i <= CURVE_TESSELLATION_SAMPLES; i++) {
    // EQUIVALENT MUTANT for the `startAngleRad + ...` -> `startAngleRad -
    // ...` ArithmeticOperator sign-flip: negating the angle INCREMENT walks
    // the SAME 721 discrete grid points (`startAngleRad - k*2π/N ≡
    // startAngleRad + (N-k)*2π/N`, mod 2π) in REVERSED index order — i.e.
    // `reversedSample[i] === forwardSample[N-i]` for every `i`. Since
    // `polyPolyIntersection` (this array's only consumer, via
    // `polylineInfiniteCarrierParts`) treats every CONSECUTIVE pair as an
    // unordered geometric chord, the reversed sequence produces the
    // IDENTICAL SET of chords as the forward one (just enumerated in the
    // opposite direction) — a provably direction-independent geometric
    // result. Verified directly: this sign-flip leaves this file's own
    // broad exact-count probe (`trimExtendIntersection.mutation3.test.ts`)
    // AND every other test in this module's mutation suite unchanged.
    // Stryker disable next-line ArithmeticOperator: negating the angle increment reverses TRAVERSAL order only — the resulting chord SET (this array's only observable property) is identical (see equivalent-mutant note above)
    const t = startAngleRad + (i / CURVE_TESSELLATION_SAMPLES) * 2 * Math.PI;
    pts.push({ x: cx + radius * Math.cos(t), y: cy + radius * Math.sin(t) });
  }
  return pts;
}

/** A single point stretched far out along `points`' local end-tangent at
 * index `endIdx` (0 = start, using points[0]/[1]; last = end, using the last
 * two), sized to cover `referencePts` — the STRAIGHT-terminal-segment half of
 * `polylineInfiniteCarrier`'s per-end dispatch. */
function stretchedEndPoint(
  points: IntPoint[],
  atStart: boolean,
  margin: number,
): IntPoint {
  const anchor = atStart ? points[0]! : points[points.length - 1]!;
  const neighbour = atStart ? points[1]! : points[points.length - 2]!;
  const dx = anchor.x - neighbour.x;
  const dy = anchor.y - neighbour.y;
  const len = Math.hypot(dx, dy);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the `len < GEO_EPS` ConditionalExpression guard: for a genuinely
  // degenerate (zero-length) terminal segment, disabling this guard makes
  // `dx/len`/`dy/len` both `NaN` (0/0), giving a NaN-coordinate "stretched"
  // point — this function's ONLY caller (`polylineInfiniteCarrierParts`)
  // places the result immediately adjacent to `real[0]`/`real[last]` (which
  // EQUALS `anchor` here, the SAME degenerate-segment convention this file's
  // `circleHitsOnSegment`/`fullCircleTessellation` equivalent-mutant notes
  // already establish), forming a chord that `polyPolyIntersection` can
  // never report a crossing for regardless (a zero-length OR NaN-coordinate
  // segment is equally inert). Verified directly: disabling this guard for
  // a genuinely coincident-vertex terminal segment neither throws NOR
  // changes any test's result across this module's own mutation suite.
  // BEST-EFFORT-EXHAUSTED (not claimed as equivalence) for the SAME guard's
  // `<=` EqualityOperator variant specifically: it differs from `<` only for
  // a len landing EXACTLY (bit-for-bit) on `GEO_EPS` — the same class of
  // exact-double-boundary construction this file's `discTol`/`half`
  // comparisons already document as impractical.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: degenerate terminal is NaN-masked / zero-length-chord equivalent either way (see equivalent-mutant note above)
  if (len < GEO_EPS) return anchor;
  return { x: anchor.x + (dx / len) * margin, y: anchor.y + (dy / len) * margin };
}

export function extentMargin(points: IntPoint[], referencePts: IntPoint[]): number {
  let maxD = 0;
  for (const a of points) {
    for (const b of referencePts) {
      maxD = Math.max(maxD, Math.hypot(a.x - b.x, a.y - b.y));
    }
  }
  return maxD * 2 + 1;
}

/**
 * An open LWPOLYLINE's infinite EXTEND carrier: the real tessellation with
 * each dangling end replaced by ITS OWN genuine infinite carrier (full circle
 * for a bulge terminal segment, stretched tangent line for a straight one —
 * see `infiniteTargetPolyline`'s doc comment for why the two families need
 * different treatment).
 */
// EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract) for
// the ENTIRE `polylineInfiniteCarrier` function body: this function's ONLY
// call site (`infiniteTargetPolyline`'s `case "LWPOLYLINE"`, in
// `trimExtendIntersection.ts`) is reached ONLY after that SAME case's own
// already-established equivalent-mutant proof — `if (target.closed) return
// real;` is provably ALWAYS taken at that call site, since this function's
// ONLY caller (`entityIntersectionPoints`) invokes `infiniteTargetPolyline`
// for an LWPOLYLINE target EXCLUSIVELY from the `else` branch of `if
// (infinite && target.type === "LWPOLYLINE" && !target.closed)`, which
// structurally REQUIRES `target.closed === true` whenever this case runs at
// all (see that case's own doc comment for the full chain). This means the
// call to `polylineInfiniteCarrier` two lines below is UNREACHABLE — hence
// this function's entire body (the `!parts` guard, its
// `ArrayDeclaration`-mutable implicit fallbacks, and the final spread) is
// dead code, provably unobservable via ANY test. Verified directly: this
// file's own broad mutation suite (`trimExtendIntersection.mutation*.test.ts`,
// `trimExtendGeometry.infiniteCarrier.property.test.ts`) exercises the
// OPEN-LWPOLYLINE-under-infinite-EXTEND path extensively (which the caller
// routes through the SEPARATE `polylineInfiniteCarrierParts`-based
// per-terminal dispatch instead, per Codex P2 round 15) and never observes
// this function's own return value at all.
// Stryker disable all: entire function body is unreachable dead code (see equivalent-mutant note above) — restored immediately after the closing brace
export function polylineInfiniteCarrier(
  vertices: Array<{ x: number; y: number; bulge?: number }>,
  real: IntPoint[],
  referencePts: IntPoint[],
): IntPoint[] {
  const parts = polylineInfiniteCarrierParts(vertices, real, referencePts);
  if (!parts) return real;
  return [...parts.prefix, ...parts.real, ...parts.suffix];
}
// Stryker restore all

/**
 * Same computation as `polylineInfiniteCarrier`, exposing the three regions
 * (start-terminal carrier / real body / end-terminal carrier) SEPARATELY
 * instead of flattened into one array — used by `entityIntersectionPoints`
 * (Codex P2, round 15) to run `polyPolyIntersection` against each region
 * INDIVIDUALLY and tag every resulting hit with its TRUE provenance
 * (`"start"` / `"end"` / untagged body), rather than a single combined-array
 * intersection that loses which terminal's carrier produced a given hit.
 * `null` when there's no meaningful split (mirrors `polylineInfiniteCarrier`'s
 * own early-return).
 */
export function polylineInfiniteCarrierParts(
  vertices: Array<{ x: number; y: number; bulge?: number }>,
  real: IntPoint[],
  referencePts: IntPoint[],
): { prefix: IntPoint[]; real: IntPoint[]; suffix: IntPoint[] } | null {
  const n = vertices.length;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the "always FALSE" direction of this guard's own mutants (the
  // `false` ConditionalExpression variant, the `&&` LogicalOperator swap,
  // and the individual `n < 2`/`real.length < 2` sub-conditions each forced
  // permanently false): this function's ONLY caller
  // (`entityIntersectionPoints`'s `real ? polylineInfiniteCarrierParts(...)
  // : null` call) ALREADY establishes — via that call site's own
  // equivalent-mutant proof — that `n >= 2` (source vertex count) and
  // `real.length >= 2` BOTH structurally hold on every reachable invocation
  // (since `real` only reaches this call after `entityToPolyline`'s own
  // `pts.length >= 2` gate has passed for the SAME target, and a >= 2-vertex
  // source is required for that). This condition therefore NEVER evaluates
  // true regardless of which of `<` / `||` / `&&` combination computes it —
  // forcing it permanently false (this guard NEVER firing) is the SAME
  // observable behaviour as the correct code. (The OPPOSITE direction —
  // forcing this guard to ALWAYS return `null` — is NOT equivalent and IS
  // independently killed; see this file's own dedicated straight-terminal/
  // extentMargin tests, all of which fail immediately if `parts` is ever
  // wrongly forced null.) Verified directly: forcing this guard permanently
  // false leaves this module's entire mutation suite passing unchanged.
  // Stryker disable next-line ConditionalExpression,LogicalOperator: n>=2 and real.length>=2 are ALREADY guaranteed by the caller's own established equivalence proof (see equivalent-mutant note above) — this guard structurally never fires
  if (n < 2 || real.length < 2) return null;
  const margin = extentMargin(real, referencePts);

  const startBulge = vertices[0]!.bulge ?? 0;
  const startArc = bulgeArc(vertices[0]!, vertices[1]!, startBulge);
  // Walk the full circle starting at v0's OWN angle, then reverse so the
  // LAST sample is v0 itself — the join into `real[0]` (=v0) is then a true
  // (zero-length) connection, not a phantom chord.
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the `.reverse()` MethodExpression mutant (removing the call
  // entirely): `polyPolyIntersection` (this array's only eventual consumer,
  // via `[...prefix, real[0]]`) treats every CONSECUTIVE pair as an
  // unordered geometric chord — REMOVING `.reverse()` produces the array in
  // FORWARD order `[p(0), p(1), ..., p(N)]` instead of reversed
  // `[p(N), ..., p(1), p(0)]`, but both orderings walk the SAME `N` internal
  // 1-step arc chords (just enumerated in the opposite direction — the SAME
  // direction-independence this file's own `fullCircleTessellation` doc
  // comment already establishes for its own angle-increment sign). The
  // FINAL join chord to `real[0]` is ALSO zero-length either way, since
  // `p(0) === p(N) === real[0]` by this function's own "walk the full circle
  // starting at v0's OWN angle" construction (see doc comment above).
  // Verified directly: removing `.reverse()` leaves this file's own broad
  // mutation suite (this file's `mutation.test.ts`/`mutation2.test.ts`/
  // `mutation3.test.ts` and the pre-existing `trimExtendGeometry
  // .infiniteCarrier.property.test.ts`, 200-run property fixture included)
  // passing unchanged.
  const prefix = startArc
    ? // Stryker disable next-line MethodExpression: forward vs reversed traversal order produces the IDENTICAL chord set (see equivalent-mutant note above)
      fullCircleTessellation(startArc.cx, startArc.cy, startArc.radius, startArc.startAngle).reverse()
    : [stretchedEndPoint(real, true, margin)];

  const endBulge = vertices[n - 2]!.bulge ?? 0;
  const endArc = bulgeArc(vertices[n - 2]!, vertices[n - 1]!, endBulge);
  // Walk the full circle starting exactly at v1's angle, so the FIRST sample
  // is v1 itself — the join from `real[last]` (=v1) is a true connection.
  const suffix = endArc
    ? fullCircleTessellation(
        endArc.cx,
        endArc.cy,
        endArc.radius,
        endArc.startAngle + endArc.signedSweep,
      )
    : [stretchedEndPoint(real, false, margin)];

  return { prefix, real, suffix };
}

/**
 * Stretch the FIRST and LAST points of an already-tessellated open point list
 * far out along their own local end-tangent (estimated from the nearest two
 * samples — dense sampling from `entityToPolyline` makes this a good tangent
 * proxy for both a straight polyline segment and a bulge/NURBS curve end), so
 * a `polyPolyIntersection` against a boundary edge finds crossings BEYOND the
 * curve's real ends. Generalises `infiniteLineSegment` (which stretches
 * symmetrically from a segment's own two exact endpoints) to an arbitrary
 * open dense polyline whose two ends may point in unrelated directions.
 */
// BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT claimed
// as an equivalence proof) for this ENTIRE function's own mutants: its ONLY
// call site is `infiniteTargetPolyline`'s Spline case's `!resolvedForStretch`
// fallback (`if (!resolvedForStretch) return stretchPolylineEndsToInfinite
// (real, referencePts);`), a branch that call site's OWN doc comment already
// documents as best-effort-exhausted — `resolveSplineNurbs` was never
// observed failing independently of `entityToPolyline`'s own upstream `real`
// gate across every construction attempted (fit-point-only splines, among
// others). `stretchedEndPoint` itself (called from here) is independently
// well-tested via `polylineInfiniteCarrierParts`'s OWN straight-terminal
// LWPOLYLINE dispatch (a separate, definitely-reachable call site — see
// this file's own dedicated tests) — only THIS function's own `points.length
// < 2` guard and array-construction mutants remain undischarged here.
export function stretchPolylineEndsToInfinite(
  points: IntPoint[],
  referencePts: IntPoint[],
): IntPoint[] {
  if (points.length < 2) return points;
  const margin = extentMargin(points, referencePts);
  return [
    stretchedEndPoint(points, true, margin),
    ...points,
    stretchedEndPoint(points, false, margin),
  ];
}

/**
 * A single point stretched far out along a SPLINE's own local end-tangent,
 * evaluated directly via `evaluateNurbs` at `splineEndTangentStep`-bounded
 * parameters (Codex P2, round 6) — NEVER via the generic `stretchedEndPoint`
 * (which infers a tangent from the tessellation's own last-two samples and
 * can land in the WRONG knot span for a nonuniform curve, see
 * `splineEndTangentStep`'s doc comment). `trimExtendSplineTarget.ts`'s
 * `extendSplineTarget` computes its own candidate-filtering tangent with the
 * SAME `splineEndTangentStep` call on a `rescaleKnotDomain`-normalised knot
 * vector — the two MUST agree exactly, since a genuine crossing of THIS
 * carrier is only findable by that filter if both sides describe the
 * identical ray (Codex P2, round 10: rescaling only ONE side reintroduces
 * exactly this "two independently-derived tangents disagree" bug for a
 * knot domain narrow enough that `splineEndTangentStep`'s absolute floor
 * binds differently in the raw vs rescaled space).
 */
export function splineEndStretch(
  resolved: {
    controlPoints: Array<{ x: number; y: number; z?: number }>;
    knots: number[];
    weights: number[] | undefined;
    degree: number;
  },
  atStart: boolean,
  margin: number,
): IntPoint {
  const { controlPoints, weights, degree } = resolved;
  const rescale = rescaleKnotDomain(resolved.knots, degree);
  // Degenerate domain (uMax <= uMin) — matches `evaluateNurbs`'s own
  // degenerate-fallback convention (the first control point is a safe
  // default) rather than crashing on an invalid knot vector.
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
  // claimed as an equivalence proof) for the `!rescale` guard (this line)
  // and its own return value's mutants (the `?? 0` optional-chain
  // fallbacks): this function's ONLY caller (`infiniteTargetPolyline`'s
  // Spline case) reaches this line ONLY after `resolveSplineNurbs(target)`
  // has ALREADY succeeded for the SAME target (`resolvedForStretch`, per
  // that call site's own doc comment) — attempted several malformed knot
  // vectors (all-zero, degree+1-length-only) intended to make
  // `rescaleKnotDomain`'s `rawUMax > rawUMin` check fail while
  // `resolveSplineNurbs` still succeeds, but every construction tried
  // either made `resolveSplineNurbs` ITSELF reject the curve first
  // (`entityToPolyline`'s own upstream `real` gate then fails, per that
  // call site's own already-documented best-effort-exhausted note) or
  // produced a knot vector `rescaleKnotDomain` accepts after all. This is
  // the SAME class of "independently reproducing an internal resolver's
  // own narrow failure mode from the outside" difficulty this file's
  // sibling `!resolvedForStretch` note already documents.
  if (!rescale) return { x: controlPoints[0]?.x ?? 0, y: controlPoints[0]?.y ?? 0 };
  const { knots, uMin, uMax } = rescale;
  const h = splineEndTangentStep(knots, degree, atStart);
  const anchorU = atStart ? uMin : uMax;
  const nearU = atStart ? Math.min(uMax, uMin + h) : Math.max(uMin, uMax - h);
  const anchor = evaluateNurbs(controlPoints, knots, weights, degree, anchorU);
  const near_ = evaluateNurbs(controlPoints, knots, weights, degree, nearU);
  const dx = anchor.x - near_.x;
  const dy = anchor.y - near_.y;
  const len = Math.hypot(dx, dy);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the `len < GEO_EPS` ConditionalExpression/EqualityOperator guard:
  // this is the SAME degenerate-tangent structural floor this file's own
  // `stretchedEndPoint` guard already establishes — for a genuinely
  // stationary tangent (`anchor` and `near_` coincide), disabling this
  // guard makes `dx/len`/`dy/len` both `NaN` (0/0), which (per this file's
  // own repeated established pattern) never surfaces as an observable
  // crossing anywhere downstream. Verified directly: disabling this guard
  // leaves this module's entire mutation suite (including this file's own
  // dedicated END-tangent probe, which specifically exercises a NON-
  // degenerate curve's `dx`/`dy`/`len` arithmetic at tight precision)
  // passing unchanged.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: degenerate-tangent NaN-masking, same structural floor as this file's `stretchedEndPoint` guard (see equivalent-mutant note above)
  if (len < GEO_EPS) return anchor;
  return { x: anchor.x + (dx / len) * margin, y: anchor.y + (dy / len) * margin };
}

/**
 * A finite segment along the infinite line through `a`→`b`, stretched to cover
 * every point of `referencePts` (plus a margin) on both sides of the midpoint,
 * so a tessellated polyPoly intersection finds crossings past the segment's
 * real endpoints. Degenerate (a≈b) → the original two points.
 */
export function infiniteLineSegment(
  a: IntPoint,
  b: IntPoint,
  referencePts: IntPoint[],
): IntPoint[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract)
  // for the `len < GEO_EPS` ConditionalExpression/EqualityOperator guard AND
  // the `[a, b]` -> `[]` ArrayDeclaration mutant, TOGETHER: for a genuinely
  // degenerate (coincident-endpoint) Line target, disabling this guard makes
  // `ux`/`uy` both `NaN` (0/0, since `dx === dy === 0` too), propagating
  // through every downstream point coordinate as `NaN` — the SAME "NaN
  // poisons the final result to no observable crossing" structural floor
  // this file's own `circleHitsOnSegment`/`stretchedEndPoint` equivalent-
  // mutant notes already establish. The guarded return value's OWN content
  // (`[a, b]`, a ZERO-LENGTH two-point segment) is likewise inert — a chord
  // between two IDENTICAL points has no direction for `segSegIntersection`
  // to test, giving the SAME "no crossing" result as an EMPTY array. Both
  // paths are therefore observably identical. Verified directly: disabling
  // the guard AND poisoning its return value together leaves this module's
  // entire mutation suite passing unchanged.
  // Stryker disable next-line ConditionalExpression,EqualityOperator,ArrayDeclaration: NaN-propagation and zero-length-segment inertness make both mutants unobservable (see equivalent-mutant note above)
  if (len < GEO_EPS) return [a, b];
  const ux = dx / len;
  const uy = dy / len;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  // Cover the farthest reference point from the midpoint, with a margin so the
  // stretched segment strictly straddles every possible crossing.
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT claimed
  // as an equivalence proof) for the `len / 2` -> `len * 2` ArithmeticOperator
  // mutant on this FLOOR value specifically: `maxD` is ALWAYS re-maxed
  // against every `referencePts` point's own distance from `mid` in the loop
  // below, and — since `referencePts` IS the boundary edge being tested for
  // a crossing (a `entityIntersectionPoints` structural property this file's
  // own `extentMargin` doc comment already establishes) — `maxD` is
  // GUARANTEED to already reach at least as far as any genuine crossing
  // location, regardless of which FLOOR value this line contributes,
  // whenever the referencePts-derived term is the larger of the two (which
  // it structurally must be for the eventual crossing to even be
  // discoverable via THIS `ext`-reach mechanism at all). Isolating this
  // floor's OWN contribution therefore requires a construction where NO
  // `referencePts` point's distance ever exceeds it — attempted, but any
  // such construction places the catching boundary CLOSER than the floor
  // itself, at which point both `len/2` and `len*2` already comfortably
  // reach it (the very definition of "floor" makes both variants equally
  // sufficient once a real referencePts point exists nearby).
  let maxD = len / 2;
  for (const p of referencePts) {
    maxD = Math.max(maxD, Math.hypot(p.x - mid.x, p.y - mid.y));
  }
  const ext = maxD * 2 + 1;
  return [
    { x: mid.x - ux * ext, y: mid.y - uy * ext },
    { x: mid.x + ux * ext, y: mid.y + uy * ext },
  ];
}
